"use client";

import MediaImage from "@/components/ui/MediaImage";

import { headerMenuPresentation } from "@/utils/header-menu-presentation";

import React, { useEffect, useMemo, useRef } from "react";
import Link from "next/link";
import MenuItem from "../../MenuItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/sharp-light-svg-icons";
import {
  headerMotion,
  useStillHeaderMenu,
} from "@/components/motion/header-motion";
import { legacyMenuVariants } from "@/utils/legacy-menu-motion";
import { headerContentWidth } from "@/utils/visual-style";
import { HeaderProps } from "../models";
import {
  getGap,
  getPaddings,
  getTextSize,
  getVisibleValue,
} from "@/utils/theme-helper";
import { twMerge } from "tailwind-merge";
import { faCircleUser } from "@fortawesome/pro-light-svg-icons";
import CtaComponent from "@/components/ui/CtaComponent";
import SearchBar from "@/components/ui/SearchBar";
import { resolveNavigation } from "@/utils/header-navigation";
import { resolveHeaderActions } from "@/utils/header-actions";
import { menuHoverMarker } from "@/utils/header-navigation";
import HeaderMenu from "@/components/navigation/HeaderMenu";
import HeaderActions from "@/components/navigation/HeaderActions";
import HeaderBottomBar from "@/components/navigation/HeaderBottomBar";
import MenuActions from "@/components/navigation/MenuActions";
import { headerLogoForState } from "@/utils/header-logos";
import { lookSchemeSettings, lookSwitchProps } from "@/utils/look-scheme";
import { withDarkLogo } from "@/utils/look-logo";
import LookSchemeSwitch from "@/components/ui/LookSchemeSwitch";
import { useHeaderState } from "../HeaderStateController";
import { isLookSystem } from "@/utils/look-gate";
import {
  headerOverlayColour,
  headerSurfacesInDark,
} from "@/utils/header-colours";
const TransparentHeader: React.FC<HeaderProps> = ({
  cmsData,
  appData,
  header,
  pageHeader,
  logoDefinition,
}) => {
  const logoUrl = appData?.logo?.originalUrl;
  const headerRef = useRef<HTMLElement>(null);
  const inverseLogoUrl = appData?.inverseLogo?.originalUrl;
  const [isVisible, setIsVisible] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);
  const [showSearch, setShowSearch] = React.useState(false);
  const view = useHeaderState();
  const shownTop = view?.settings.top;
  const lookSystem = isLookSystem(cmsData?.theme);
  const { motion, AnimatePresence } = headerMotion(lookSystem);
  const stillMenu = useStillHeaderMenu(lookSystem);
  const menuMotion = legacyMenuVariants(stillMenu);
  const managed = !!view;
  const scrolled = view ? view.frame.scrolled || showSearch : isVisible;
  const solidFromTop = view ? view.settings.top === "solid" : header.isVisible;

  const menuItems = cmsData?.pages
    .filter((x) => x.includeInMenu && x.type !== "article")
    .map((page) => ({
      title: page.menuTitle,
      href: page.slug,
      redirectToExternal: page.redirectToExternal,
      openInNewTab: page.openInNewTab,
      externalUrl: page.externalUrl,
    }));

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const toggleSearch = () => {
    if (navigation) setShowMenu(false);
    if (!showSearch) {
      setIsVisible(true);
    } else {
      setIsVisible(window.scrollY > (header?.scrollThreshold ?? 1));
    }
    setShowSearch(!showSearch);
  };

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape" && showSearch) {
      toggleSearch();
    }
    if (event.key === "Escape" && showMenu) {
      setShowMenu(false);
    }
  }

  function handleScroll() {
    if (!window) {
      return;
    }

    if (window.scrollY > (header?.scrollThreshold ?? 1)) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSearch, showMenu]);

  useEffect(() => {
    if (!window || managed) {
      return;
    }

    if (window.scrollY > (header?.scrollThreshold ?? 1)) {
      setIsVisible(true);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [managed]);

  let customerPortalUrl =
    cmsData?.b2cLink ?? appData?.resourceGroup?.primaryB2CApplication?.url;
  if (customerPortalUrl && !customerPortalUrl.startsWith("https://")) {
    customerPortalUrl = "https://" + customerPortalUrl.replace("http://", "");
  }

  const heightMobile = header.heightMobile || "80px";
  const heightDesktop = header.heightDesktop || heightMobile;
  const heightMobileVisible = header.heightMobileVisible || "60px";
  const heightDesktopVisible =
    header.heightDesktopVisible || heightMobileVisible;
  const menuIconName = cmsData?.subdomain;
  const isSecondaryLayout = cmsData?.layout?.name === "secondary";
  const navigation = useMemo(
    () =>
      resolveNavigation({
        site: cmsData,
        pages: cmsData?.pages ?? [],
        type: header.type,
        root: cmsData?.layout?.header,
        page: pageHeader,
        colours: logoDefinition?.colours,
        top: shownTop,
      }),
    [cmsData, header.type, pageHeader, logoDefinition, shownTop],
  );
  const { bar: barInDark, panel: panelInDark } = headerSurfacesInDark(
    cmsData?.theme,
    {
      type: header.type,
      config: cmsData?.layout?.header,
      page: pageHeader,
      top: shownTop,
    },
    logoDefinition?.colours,
  );
  const actions = useMemo(
    () =>
      Array.isArray(header.actions)
        ? resolveHeaderActions(header.actions, {
            pages: cmsData?.pages ?? [],
            portalUrl: customerPortalUrl,
            theme: cmsData?.theme,
            surfaces: { bar: barInDark, panel: panelInDark },
          })
        : undefined,
    [header.actions, cmsData, customerPortalUrl, barInDark, panelInDark],
  );
  const actionIcons = {
    search: (
      <FontAwesomeIcon className="size-[24px] flex" icon={faMagnifyingGlass} />
    ),
    user: <FontAwesomeIcon className="size-[24px] flex" icon={faCircleUser} />,
  };
  const headerSwitch = lookSwitchProps(cmsData, "header");
  const menuSwitch = lookSwitchProps(cmsData, "menu");

  const logoInput = {
    website: cmsData ?? {},
    definition: logoDefinition,
    config: view
      ? {
          ...header,
          isVisible: solidFromTop,
          type: "TransparentHeader",
        }
      : { ...header, type: "TransparentHeader" },
    media: { logo: logoUrl, inverseLogo: inverseLogoUrl },
  };
  const logoFor = (switched: boolean) =>
    headerLogoForState(logoInput, { scrolled: switched }).logo.url;
  const stateLogos = view
    ? { top: logoFor(false), switched: logoFor(true) }
    : undefined;
  const bothLogos = !!stateLogos && stateLogos.top !== stateLogos.switched;
  const logoImage = (
    src: string | undefined,
    marker?: { "data-re-logo": string; alt?: string },
  ) =>
    withDarkLogo(
      <MediaImage
        data-isvisible={(scrolled || solidFromTop) ?? false}
        suppressHydrationWarning={managed}
        src={src}
        alt="logo"
        data-showmenu={showMenu}
        height={0}
        width={150}
        style={
          {
            "--heightMobile": heightMobile,
            "--heightDesktop": heightDesktop,
            "--heightMobileVisible": heightMobileVisible,
            "--heightDesktopVisible": heightDesktopVisible,
          } as React.CSSProperties
        }
        className={`py-4 
                  h-[var(--heightMobile)] 
                  data-[isvisible=true]:h-[var(--heightMobileVisible)] 
                  md:h-[var(--heightDesktop)] 
                  data-[isvisible=true]:md:h-[var(--heightDesktopVisible)] 
                  object-contain data-[showmenu=true]:hidden 
                  data-[showmenu=true]:md:flex 
                  transition-all duration-200 
                  w-auto ${cmsData?.layout?.header?.paddingTransparent ? getPaddings(cmsData?.layout?.header?.paddingTransparent) : ""}`}
        {...marker}
      />,
      lookSchemeSettings(cmsData?.theme)?.logo,
    );

  const logo = (
    <Link
      aria-label="Home"
      className={`flex ${cmsData?.layout?.header?.padding ? getPaddings(cmsData?.layout?.header?.padding) : ""} group-data-[centerlogo=true]:md:col-start-2`}
      href={"/"}
      data-re-header-logos={bothLogos ? "" : undefined}
    >
      {(logoDefinition?.logoUrl ||
        header.logoOverrides?.primary ||
        cmsData?.logoUrl ||
        logoUrl) &&
        (!stateLogos ? (
          logoImage(logoFor(scrolled))
        ) : bothLogos ? (
          <>
            {logoImage(stateLogos.top, { "data-re-logo": "top" })}
            {logoImage(stateLogos.switched, {
              "data-re-logo": "switched",
              alt: "",
            })}
          </>
        ) : (
          logoImage(stateLogos.switched, { "data-re-logo": "" })
        ))}
    </Link>
  );
  const legacyActions = (
    <>
      {cmsData?.layout?.header?.ctas?.map((x, index) => (
        <CtaComponent
          key={`header-cta-${index}`}
          id={`header-cta-${index}`}
          config={isLookSystem(cmsData.theme) ? { ...x, lookSystem: true } : x}
          itemIndex={index}
        />
      ))}
      <button
        data-hideicon={showMenu || showSearch}
        data-isvisible={(scrolled || solidFromTop) ?? false}
        suppressHydrationWarning={managed}
        data-menu-open={showMenu}
        data-foo="Asd"
        aria-label="Search"
        className={twMerge(
          "p-[8px] data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
          "data-[menu-open=true]:text-[var(--menu-text-color)]",
          getVisibleValue(cmsData?.layout?.header?.showSearch),
        )}
        onClick={() => toggleSearch()}
      >
        <FontAwesomeIcon
          className="size-[24px] flex"
          icon={faMagnifyingGlass}
        />
      </button>
      {customerPortalUrl && (
        <Link
          data-hideicon={showMenu || showSearch}
          data-menu-open={showMenu}
          aria-label="Kundeunivers"
          data-isvisible={(scrolled || solidFromTop) ?? false}
          suppressHydrationWarning={managed}
          target="_blank"
          href={customerPortalUrl}
          className={twMerge(
            "p-[8px] data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
            "data-[menu-open=true]:text-[var(--menu-text-color)]",
            getVisibleValue(cmsData?.layout?.header?.showB2c),
          )}
        >
          <FontAwesomeIcon className="size-[24px] flex" icon={faCircleUser} />
        </Link>
      )}
    </>
  );

  return (
    <div id="TransparentHeader" className="relative">
      {header.overlay && !showSearch && (
        <div
          style={
            {
              backgroundImage: `linear-gradient(to bottom, ${headerOverlayColour(cmsData?.theme, header.overlay.fromBgColor)}, rgba(255,0,0,0))`,
              "--heightMobile": heightMobile,
              "--heightDesktop": heightDesktop,
            } as React.CSSProperties
          }
          className={`z-30 absolute inset-0 h-[var(--heightMobile)] md:h-[var(--heightDesktop)]`}
        ></div>
      )}

      <header
        ref={headerRef}
        data-re-menu-open={(navigation && showMenu) || undefined}
        data-re-menu-style={navigation?.options.style}
        data-isvisible={scrolled}
        suppressHydrationWarning={managed}
        data-isvisiblefrompage={solidFromTop}
        data-re-header={managed ? "transparent" : undefined}
        style={
          {
            "--heightMobile": heightMobile,
            "--heightDesktop": heightDesktop,
            "--heightMobileVisible": heightMobileVisible,
            "--heightDesktopVisible": heightDesktopVisible,
          } as React.CSSProperties
        }
        className={`
          transition-[height] 
          ease-in-out 
          duration-200 
          fixed 
          top-0 
          left-0 
          right-0 
          z-30
          flex 
          justify-between 

          h-[var(--heightMobile)] 
          data-[isvisible=true]:h-[var(--heightMobileVisible)] 
          md:h-[var(--heightDesktop)] 
          data-[isvisible=true]:md:h-[var(--heightDesktopVisible)] 

          data-[isvisible=true]:shadow-md 
          data-[isvisible=true]:bg-[var(--header-bg-color)] 
          data-[isvisible=false]:text-[var(--header-text-color)] 
          data-[isvisible=true]:text-[var(--header-visible-text-color)] 
          data-[isvisible=false]:bg-transparent
          
          data-[isvisiblefrompage=true]:shadow-md 
          data-[isvisiblefrompage=true]:bg-[var(--header-bg-color)] 
          data-[isvisiblefrompage=true]:text-[var(--header-visible-text-color)] 
          `}
      >
        <div
          className={`fixed inset-0 bg-gray-800 z-20 transition-opacity duration-300 ${
            showMenu && !navigation
              ? "opacity-40"
              : "opacity-0 pointer-events-none"
          }`}
        ></div>
        <AnimatePresence>
          {showMenu && !navigation && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="hidden"
              className={`flex shadow-md text-[var(--menu-text-color)] h-dvh fixed top-0 z-20 right-0 left-0 lg:left-auto bottom-0 lg:w-[380px] pt-28 pl-[30px]`}
            >
              {/* Animating clipPath itself restores the open circle for one frame before the closed menu is removed; a custom property keeps the last frame. */}
              <motion.div
                className="bg-[var(--menu-bg-color)] absolute inset-0 z-20"
                style={{
                  clipPath:
                    "circle(var(--re-menu-circle, 0%) at calc(100% - 54px) 50px)",
                }}
                variants={menuMotion.circle}
              ></motion.div>
              <motion.div
                variants={menuMotion.list}
                className={`flex flex-col gap-4 relative z-20 flex-1 overflow-y-auto pb-10 ${getTextSize(header.menuTextSize, "lg")} ${isSecondaryLayout ? "primary-font" : ""}`}
              >
                {menuItems?.map((item, index) => (
                  <motion.div key={`${index}-menu`} variants={menuMotion.item}>
                    <MenuItem
                      key={item.href}
                      href={item.href}
                      onClick={() => setShowMenu(false)}
                      redirectToExternal={item.redirectToExternal}
                      openInNewTab={item.openInNewTab}
                      externalUrl={item.externalUrl}
                      customIconName={menuIconName}
                    >
                      {item.title}
                    </MenuItem>
                  </motion.div>
                ))}
                {actions && (
                  <MenuActions
                    actions={actions}
                    bp="lg"
                    icons={actionIcons}
                    onSearch={toggleSearch}
                    onNavigate={() => setShowMenu(false)}
                  />
                )}
                {menuSwitch && (
                  <LookSchemeSwitch
                    {...menuSwitch}
                    className="text-[var(--menu-text-color)]"
                  />
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {navigation ? (
          <HeaderMenu
            lookSystem={lookSystem}
            open={showMenu}
            onOpenChange={setShowMenu}
            presentation={{
              ...headerMenuPresentation(header),
              solid: !!(scrolled || solidFromTop),
            }}
            inheritedTextClassName={`${getTextSize(header.menuTextSize, "lg")} ${isSecondaryLayout ? "primary-font" : ""}`}
            navigation={navigation}
            hoverMarker={menuHoverMarker(menuIconName)}
            actions={actions}
            legacyActions={legacyActions}
            logo={logo}
            logoPosition={
              header.logoPlacement === "center" ? "center" : "start"
            }
            rowClassName={`re-nav-row ${getGap(cmsData?.layout?.header?.gap)}`}
            rowStyle={headerContentWidth(header.maxWidth)}
            bp="lg"
            appearance="default"
            anchor={headerRef}
            onSearch={toggleSearch}
            icons={actionIcons}
            schemeSwitch={
              headerSwitch && <LookSchemeSwitch {...headerSwitch} />
            }
            panelExtras={menuSwitch && <LookSchemeSwitch {...menuSwitch} />}
          />
        ) : (
          <div
            data-centerlogo={header.logoPlacement === "center"}
            style={headerContentWidth(header.maxWidth)}
            className={`px-[20px] lg:px-[40px] ${getGap(cmsData?.layout?.header?.gap)} grid grid-cols-2 data-[centerlogo=true]:md:grid-cols-[1fr_auto_1fr] z-30 items-center flex-1 group`}
          >
            {logo}
            <div className="flex gap-2 md:gap-8 items-center justify-end">
              {actions ? (
                <HeaderActions
                  actions={actions}
                  bp="lg"
                  icons={actionIcons}
                  onSearch={toggleSearch}
                />
              ) : (
                legacyActions
              )}
              {headerSwitch && (
                <LookSchemeSwitch
                  {...headerSwitch}
                  data-isvisible={scrolled || solidFromTop}
                  suppressHydrationWarning={managed}
                  data-menu-open={showMenu}
                  className="data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)] data-[menu-open=true]:text-[var(--menu-text-color)]"
                />
              )}
              <button
                aria-label="Menu"
                onClick={() => (showSearch ? toggleSearch() : toggleMenu())}
                className=" size-10 relative focus:outline-none rounded group"
              >
                <div className="block w-7 absolute left-6 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span
                    data-isvisible={scrolled || solidFromTop || showMenu}
                    suppressHydrationWarning={managed}
                    data-menu-open={showMenu}
                    className={twMerge(
                      "block absolute right-0 h-0.5 data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)] group-hover:w-full bg-current transition-all transform duration-300 ease-in-out",
                      "data-[menu-open=true]:text-[var(--menu-text-color)]",
                      showMenu || showSearch
                        ? "rotate-45 w-full "
                        : "-translate-y-2 w-[70%]",
                    )}
                  ></span>
                  <span
                    data-isvisible={scrolled || solidFromTop || showMenu}
                    suppressHydrationWarning={managed}
                    data-menu-open={showMenu}
                    className={twMerge(
                      "block absolute right-0 h-0.5  group-hover:w-full bg-current  transition-all  transform duration-300 ease-in-out data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
                      "data-[menu-open=true]:text-[var(--menu-text-color)]",
                      showMenu || showSearch ? "opacity-0" : "w-[60%]",
                    )}
                  ></span>
                  <span
                    data-isvisible={scrolled || solidFromTop || showMenu}
                    suppressHydrationWarning={managed}
                    data-menu-open={showMenu}
                    className={twMerge(
                      "block absolute right-0 h-0.5 w-full bg-current transform  transition duration-300 ease-in-out data-[isvisible=false]:text-[var(--header-text-color)] data-[isvisible=true]:text-[var(--header-visible-text-color)]",
                      "data-[menu-open=true]:text-[var(--menu-text-color)]",
                      showMenu || showSearch ? "-rotate-45" : "translate-y-2",
                    )}
                  ></span>
                </div>
              </button>
            </div>
          </div>
        )}
      </header>
      {actions && (
        <HeaderBottomBar
          actions={actions}
          bp="lg"
          icons={actionIcons}
          onSearch={toggleSearch}
        />
      )}
      <SearchBar
        cmsData={cmsData!}
        toggleSearch={() => toggleSearch()}
        showSearch={showSearch}
      />
    </div>
  );
};

export default TransparentHeader;
